import React from 'react';
import type { TextInput } from 'react-native';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

import Check from '@rover/icons/svg/check.svg';
import type { RoverTheme } from '@rover/types/src/RoverTheme';
import type { SvgIcon } from '@rover/types/src/SvgIcon';

import { Box, Flex, Text } from '../../../core';
import { SpacingSystemType } from '../../../core/styledSystemPropTypes';
import { ControlSide, KibbleFormFieldComponentCommonProps } from '../../types';
import { SharedTypographyProps } from '../sharedTypography';

/** A height, or one height per breakpoint. */
export type TextareaMinHeightType = string | [string, string] | [string, string, string];

/** Options for the single-line `default` variant. */
type DefaultVariantOptions = {
  /**
   * The number of characters typed, shown under the field as "12 characters". Pass
   * `watch('key')?.length ?? 0`, with `watch` from `useKibbleForm`. It doesn't limit the length.
   */
  currentCharacterCount?: number;
};

/** Options for the multi-line `textarea` variant. */
type TextareaVariantOptions = {
  /** The textarea's minimum height, such as `'120px'`. Defaults to `12x` (48px). */
  minHeight?: TextareaMinHeightType;
  /**
   * The number of words the answer needs, shown as a countdown with `currentWordCount`. The field
   * doesn't validate it, so check it in the schema too.
   */
  minimumWordCount?: number;
  /**
   * The number of words typed. Pass `getWordCount(watch('key') ?? '') ?? 0`, with `getWordCount`
   * from `@rover/utilities/wordCount` and `watch` from `useKibbleForm`.
   */
  currentWordCount?: number;
};

/** `variantOptions` takes the options that belong to the chosen `variant`. */
export type VariantProps =
  | {
      /** `textarea` takes an answer over several lines. Defaults to `default`, a single line. */
      variant?: 'default';
      /**
       * `currentCharacterCount` for `default`; `minHeight`, `minimumWordCount` and
       * `currentWordCount` for `textarea`.
       */
      variantOptions?: DefaultVariantOptions;
    }
  | {
      variant: 'textarea';
      variantOptions?: TextareaVariantOptions;
    };

const isDefaultVariantOptions = (
  variantOptions: VariantProps['variantOptions']
): variantOptions is DefaultVariantOptions => {
  return (variantOptions as DefaultVariantOptions).currentCharacterCount !== undefined;
};

export const isTextareaVariantOptions = (
  variantOptions: VariantProps['variantOptions']
): variantOptions is TextareaVariantOptions => {
  const opts = variantOptions as TextareaVariantOptions;
  return !!(opts.minimumWordCount || opts.currentWordCount || opts.minHeight);
};

const characterAndWordCountCommonTextStyles = {
  mt: '1x',
  size: '100',
  textAlign: 'right',
  textColor: 'tertiary',
} as const;

type CharacterAndWordCountProps = {
  variant: VariantProps['variant'];
  variantOptions: VariantProps['variantOptions'];
};

/**
 * The props TextField takes on web and React Native. Spread `configureField()` from
 * `useKibbleForm` onto the field to connect it to a form.
 */
export type TextFieldCommonProps = KibbleFormFieldComponentCommonProps &
  SharedTypographyProps &
  VariantProps & {
    /**
     * Width of the input, such as `'240px'`. The labels and messages keep the container's width.
     * Defaults to `100%` on web.
     */
    fieldWidth?: string;
    /** A decorative icon inside the input, such as a calendar. Screen readers skip it. */
    icon?: SvgIcon;
    /** The side of the input the icon sits on. Defaults to `right`. */
    iconAlignment?: ControlSide;
    /** Text shown while the input is empty. It disappears once someone types. */
    placeholder?: string;
    /** Shows the value on a gray background and stops it being edited. */
    readOnly?: boolean;
    /**
     * A translated error message to show in place of `fieldError`'s. It marks the field invalid.
     */
    translatedFieldErrorMessage?: string;
    /** Receives the web input element, such as to focus it. */
    webInputRef?: React.RefCallback<HTMLElement>;
    /** Receives the React Native `TextInput`, such as to focus it. */
    rxnInputRef?: React.RefCallback<TextInput>;
    /** Space below the field. Defaults to `6x`. */
    marginBottom?: SpacingSystemType;
  };

export const getBorderColor = (
  colors: RoverTheme['colors'],
  hasError?: boolean,
  disabled?: boolean
): string => {
  if (hasError) return colors.border.error;
  if (disabled) return colors.interactive_link.secondary_inverse_pressed;

  return colors.interactive_border.primary_base;
};

export const getBackgroundColor = (
  theme: RoverTheme,
  disabled?: boolean,
  readOnly?: boolean
): string => {
  if (disabled) return theme.colors.interactive_border.disabled;
  if (readOnly) return theme.background.interactive.disabled;

  return theme.background.interactive.primary_base;
};

/** Props for TextField icon display */
export interface TextFieldIconProps {
  /** Icon component to render */
  icon?: SvgIcon;
  /** Position of icon (left or right) */
  iconAlignment: NonNullable<TextFieldCommonProps['iconAlignment']>;
  textColor: RoverTheme['textColor'];
  /** Disabled state affects icon color */
  disabled?: boolean;
}

export const TextFieldIcon = ({
  icon: Icon,
  iconAlignment,
  textColor,
  disabled,
}: TextFieldIconProps): JSX.Element | null => {
  if (!Icon) return null;

  const testingID = process.env.JS_ENV_NATIVE ? 'testID' : 'data-testid';
  const flexProps = {
    alignItems: 'center',
    [testingID]: 'kff-text-field-icon',
    position: 'absolute' as const,
    [iconAlignment]: '2x',
  };
  const iconColor = disabled ? textColor.disabled : textColor.secondary;

  return (
    <Flex {...flexProps}>
      <Icon height="24px" width="24px" aria-hidden fill={iconColor.toString()} />
    </Flex>
  );
};

export const CharacterCount = ({
  variant,
  variantOptions,
}: CharacterAndWordCountProps): JSX.Element | null => {
  const { t } = useLingui();
  if (
    variant === 'default' &&
    isDefaultVariantOptions(variantOptions) && // kinda silly, but TS needs this to refine variantOptions
    variantOptions.currentCharacterCount !== undefined // 0 is falsy, so we need to check "not undefined"
  ) {
    return (
      <Text {...characterAndWordCountCommonTextStyles} data-testid="character-count-message">
        {variantOptions.currentCharacterCount}{' '}
        {t({
          message: plural(variantOptions.currentCharacterCount, {
            one: 'character',
            other: 'characters',
          }),
        })}
      </Text>
    );
  }
  return null;
};

export const WordCount = ({
  variant,
  variantOptions,
}: CharacterAndWordCountProps): JSX.Element | null => {
  const { t } = useLingui();
  if (
    variant === 'textarea' &&
    isTextareaVariantOptions(variantOptions) && // kinda silly, but TS needs this to refine variantOptions
    variantOptions.minimumWordCount &&
    variantOptions.currentWordCount !== undefined // 0 is falsy, so we need to check "not undefined"
  ) {
    const numWordsLeft = variantOptions.minimumWordCount - variantOptions.currentWordCount;

    if (numWordsLeft < 1) {
      return (
        <Flex
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-end"
          mt="1x"
          data-testid="word-count-message-check"
        >
          <Box
            webAs={Check}
            rxnAs={Check}
            height="16px"
            width="16px"
            sx={{
              fill: 'text.success',
              stroke: 'text.success',
              strokeWidth: '4px',
            }}
          />
        </Flex>
      );
    }
    return (
      <Text {...characterAndWordCountCommonTextStyles} data-testid="word-count-message">
        {numWordsLeft === variantOptions.minimumWordCount
          ? t`${numWordsLeft} words minimum`
          : t({
              message: plural(numWordsLeft, {
                one: '# more word needed',
                other: '# more words needed',
              }),
            })}
      </Text>
    );
  }
  return null;
};
